// src/utils/textProcessing.js
// All text/document analysis in pure JS — no libraries, no stubs
// Everything computes from raw text

// ─────────────────────────────────────────────
// TOKENIZATION
// ─────────────────────────────────────────────

export const tokenize = (text) => {
  return text.toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, ' ')
    .split(/\s+/)
    .filter(t => t.length > 0);
};

export const tokenizeSentences = (text) => {
  // Split on . ! ? followed by space + capital or end of string
  return text
    .replace(/([.!?])\s+([A-Z])/g, '$1\n$2')
    .split('\n')
    .map(s => s.trim())
    .filter(s => s.length > 5);
};

export const tokenizeParagraphs = (text) => {
  return text.split(/\n\s*\n/).map(p => p.trim()).filter(p => p.length > 0);
};

// ─────────────────────────────────────────────
// BASIC STATS
// ─────────────────────────────────────────────

export const getBasicStats = (text) => {
  const words = tokenize(text);
  const sentences = tokenizeSentences(text);
  const paragraphs = tokenizeParagraphs(text);
  const chars = text.length;
  const charsNoSpaces = text.replace(/\s/g, '').length;
  const lines = text.split('\n').length;
  const uniqueWords = new Set(words).size;
  const avgWordLen = words.reduce((s, w) => s + w.length, 0) / (words.length || 1);
  const avgSentLen = sentences.length > 0 ? words.length / sentences.length : 0;
  const avgParaLen = paragraphs.length > 0 ? sentences.length / paragraphs.length : 0;

  return {
    chars, charsNoSpaces, words: words.length, uniqueWords,
    sentences: sentences.length, paragraphs: paragraphs.length,
    lines, avgWordLen: +avgWordLen.toFixed(2),
    avgSentLen: +avgSentLen.toFixed(1),
    avgParaLen: +avgParaLen.toFixed(1),
    lexicalDiversity: +(uniqueWords / (words.length || 1)).toFixed(4),
    estimatedReadTime: Math.ceil(words.length / 200), // 200wpm
  };
};

// ─────────────────────────────────────────────
// WORD FREQUENCY
// ─────────────────────────────────────────────

const STOPWORDS = new Set([
  'the','a','an','and','or','but','in','on','at','to','for','of','with',
  'is','are','was','were','be','been','being','have','has','had','do',
  'does','did','will','would','could','should','may','might','shall',
  'can','need','dare','ought','used','it','its','this','that','these',
  'those','i','me','my','myself','we','our','you','your','he','him','his',
  'she','her','they','them','their','what','which','who','whom','how',
  'when','where','why','all','each','every','both','few','more','most',
  'other','some','such','no','not','only','same','so','than','too','very',
  'just','as','up','down','from','by','about','into','through','during',
  'before','after','above','below','if','then','because','while','although',
  'after','also','back','out','get','go','there','here','now','also','well',
  'any','one','two','new','even','like','over','still','first','think','know',
  'make','take','come','see','look','time','work','way','day','year','man',
  'people','say','said','s','t','re','ve','ll','d','m'
]);

export const getWordFrequency = (text, topN = 50, excludeStopwords = true) => {
  const tokens = tokenize(text);
  const freq = {};
  tokens.forEach(t => {
    if (t.length < 2) return;
    if (excludeStopwords && STOPWORDS.has(t)) return;
    freq[t] = (freq[t] || 0) + 1;
  });
  return Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([word, count]) => ({ word, count, pct: +(count / tokens.length * 100).toFixed(2) }));
};

export const getBigrams = (text, topN = 30) => {
  const tokens = tokenize(text).filter(t => t.length > 1 && !STOPWORDS.has(t));
  const bigrams = {};
  for (let i = 0; i < tokens.length - 1; i++) {
    const bg = `${tokens[i]} ${tokens[i+1]}`;
    bigrams[bg] = (bigrams[bg] || 0) + 1;
  }
  return Object.entries(bigrams)
    .filter(([, c]) => c > 1)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([bigram, count]) => ({ bigram, count }));
};

export const getTrigrams = (text, topN = 20) => {
  const tokens = tokenize(text).filter(t => t.length > 1 && !STOPWORDS.has(t));
  const trigrams = {};
  for (let i = 0; i < tokens.length - 2; i++) {
    const tg = `${tokens[i]} ${tokens[i+1]} ${tokens[i+2]}`;
    trigrams[tg] = (trigrams[tg] || 0) + 1;
  }
  return Object.entries(trigrams)
    .filter(([, c]) => c > 1)
    .sort((a, b) => b[1] - a[1])
    .slice(0, topN)
    .map(([trigram, count]) => ({ trigram, count }));
};

// ─────────────────────────────────────────────
// READABILITY SCORES
// ─────────────────────────────────────────────

const countSyllables = (word) => {
  word = word.toLowerCase().replace(/[^a-z]/g, '');
  if (word.length <= 3) return 1;
  word = word.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '');
  word = word.replace(/^y/, '');
  const m = word.match(/[aeiouy]{1,2}/g);
  return m ? m.length : 1;
};

export const getReadabilityScores = (text) => {
  const words = tokenize(text);
  const sentences = tokenizeSentences(text);
  const W = words.length, S = Math.max(1, sentences.length);
  const syllables = words.reduce((s, w) => s + countSyllables(w), 0);
  const complexWords = words.filter(w => countSyllables(w) >= 3).length;
  const longWords = words.filter(w => w.length > 6).length;

  // Flesch Reading Ease (0-100, higher = easier)
  const flesch = 206.835 - 1.015 * (W / S) - 84.6 * (syllables / W);

  // Flesch-Kincaid Grade Level
  const fkGrade = 0.39 * (W / S) + 11.8 * (syllables / W) - 15.59;

  // Gunning Fog Index
  const fog = 0.4 * (W / S + 100 * complexWords / W);

  // SMOG Index (needs ≥ 30 sentences for accuracy)
  const smog = 3 + Math.sqrt(complexWords * (30 / S));

  // Coleman-Liau Index
  const L = (text.replace(/\s/g, '').length / W) * 100;
  const N = (S / W) * 100;
  const cli = 0.0588 * L - 0.296 * N - 15.8;

  // Automated Readability Index
  const ari = 4.71 * (text.replace(/\s/g,'').length / W) + 0.5 * (W / S) - 21.43;

  // Dale-Chall (simplified)
  const hardWords = words.filter(w => countSyllables(w) > 1 && w.length > 4).length;
  const daleChall = 0.1579 * (hardWords / W * 100) + 0.0496 * (W / S);

  const gradeLabel = (g) => {
    g = Math.round(g);
    if (g <= 5) return 'Elementary';
    if (g <= 8) return 'Middle School';
    if (g <= 12) return 'High School';
    if (g <= 16) return 'College';
    return 'Graduate';
  };

  return {
    flesch: +flesch.toFixed(1),
    fleschLabel: flesch > 70 ? 'Easy' : flesch > 50 ? 'Moderate' : flesch > 30 ? 'Difficult' : 'Very Difficult',
    fkGrade: +fkGrade.toFixed(1),
    fkLabel: gradeLabel(fkGrade),
    fog: +fog.toFixed(1),
    fogLabel: gradeLabel(fog),
    smog: +smog.toFixed(1),
    cli: +cli.toFixed(1),
    ari: +ari.toFixed(1),
    daleChall: +daleChall.toFixed(2),
    syllables, complexWords, longWords,
    syllablesPerWord: +(syllables / W).toFixed(2),
    complexWordPct: +(complexWords / W * 100).toFixed(1),
  };
};

// ─────────────────────────────────────────────
// SENTIMENT ANALYSIS (lexicon-based)
// ─────────────────────────────────────────────

const POSITIVE_WORDS = new Set([
  'good','great','excellent','amazing','wonderful','fantastic','awesome','superb',
  'love','loved','loving','like','liked','happy','joy','joyful','positive',
  'best','better','brilliant','outstanding','perfect','beautiful','incredible',
  'successful','success','win','winning','won','achieve','achieved','proud',
  'benefit','beneficial','helpful','useful','valuable','important','significant',
  'strong','powerful','effective','efficient','smart','innovative','creative',
  'clear','easy','simple','fast','quick','safe','secure','reliable','trust',
  'honest','fair','kind','generous','genuine','authentic','real','true',
  'excited','exciting','inspired','inspiring','motivating','engaging','fun',
  'reward','rewarding','delighted','pleased','satisfied','confident','hope',
  'hopeful','optimistic','enthusiastic','passionate','dedicated','committed'
]);

const NEGATIVE_WORDS = new Set([
  'bad','terrible','awful','horrible','dreadful','worst','poor','weak','wrong',
  'hate','hated','angry','sad','upset','unhappy','disappointed','frustrating',
  'frustrated','annoyed','annoying','boring','bored','useless','waste','wasted',
  'fail','failed','failure','error','mistake','problem','issue','bug','broken',
  'hard','difficult','complex','slow','late','delayed','missed','missing',
  'lack','lacking','absent','incomplete','inadequate','insufficient','limited',
  'dangerous','risk','risky','unsafe','insecure','unreliable','untrusted',
  'expensive','costly','overpriced','cheap','inferior','mediocre','average',
  'ugly','messy','cluttered','complicated','confusing','unclear','vague',
  'fear','fearful','worried','concerned','anxious','stressed','overwhelmed',
  'reject','rejected','denied','blocked','prevented','stopped','cancelled'
]);

const INTENSIFIERS = new Set(['very','extremely','highly','incredibly','absolutely','completely','totally','utterly','remarkably','exceptionally']);
const NEGATORS = new Set(['not','no','never','neither','nor','nothing','nobody','nowhere','hardly','barely','scarcely','without']);

export const getSentiment = (text) => {
  const sentences = tokenizeSentences(text);
  const words = tokenize(text);

  let posCount = 0, negCount = 0, intensifierCount = 0, negatorCount = 0;
  let sentenceScores = [];

  sentences.forEach(sent => {
    const sWords = tokenize(sent);
    let sPos = 0, sNeg = 0;
    sWords.forEach((w, i) => {
      const prevWord = i > 0 ? sWords[i - 1] : '';
      const prevPrev = i > 1 ? sWords[i - 2] : '';
      const isNegated = NEGATORS.has(prevWord) || NEGATORS.has(prevPrev);
      const multiplier = INTENSIFIERS.has(prevWord) ? 1.5 : 1;
      if (POSITIVE_WORDS.has(w)) {
        if (isNegated) sNeg += multiplier;
        else sPos += multiplier;
      }
      if (NEGATIVE_WORDS.has(w)) {
        if (isNegated) sPos += multiplier * 0.5;
        else sNeg += multiplier;
      }
      if (INTENSIFIERS.has(w)) intensifierCount++;
      if (NEGATORS.has(w)) negatorCount++;
    });
    const total = sPos + sNeg;
    const score = total > 0 ? (sPos - sNeg) / total : 0;
    sentenceScores.push({
      text: sent.slice(0, 80) + (sent.length > 80 ? '...' : ''),
      score: +score.toFixed(3),
      label: score > 0.15 ? 'positive' : score < -0.15 ? 'negative' : 'neutral',
      pos: sPos, neg: sNeg,
    });
    posCount += sPos; negCount += sNeg;
  });

  words.forEach(w => {
    if (POSITIVE_WORDS.has(w)) posCount++;
    if (NEGATIVE_WORDS.has(w)) negCount++;
  });

  const total = posCount + negCount;
  const overallScore = total > 0 ? (posCount - negCount) / total : 0;

  return {
    overallScore: +overallScore.toFixed(3),
    label: overallScore > 0.15 ? 'Positive' : overallScore < -0.15 ? 'Negative' : 'Neutral',
    posCount: Math.round(posCount), negCount: Math.round(negCount),
    intensifierCount, negatorCount,
    positiveRatio: +(posCount / (total || 1) * 100).toFixed(1),
    negativeRatio: +(negCount / (total || 1) * 100).toFixed(1),
    sentenceScores: sentenceScores.slice(0, 40),
    positiveSentences: sentenceScores.filter(s => s.label === 'positive').length,
    negativeSentences: sentenceScores.filter(s => s.label === 'negative').length,
    neutralSentences: sentenceScores.filter(s => s.label === 'neutral').length,
  };
};

// ─────────────────────────────────────────────
// NAMED ENTITY RECOGNITION (pattern-based)
// ─────────────────────────────────────────────

export const extractEntities = (text) => {
  const entities = { PERSON: [], ORG: [], LOCATION: [], DATE: [], NUMBER: [], EMAIL: [], URL: [], MONEY: [], PERCENT: [] };

  // Email
  const emails = text.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g) || [];
  entities.EMAIL = [...new Set(emails)];

  // URL
  const urls = text.match(/https?:\/\/[^\s]+|www\.[^\s]+/g) || [];
  entities.URL = [...new Set(urls)];

  // Money
  const money = text.match(/\$[\d,]+(?:\.\d{2})?(?:\s*(?:million|billion|thousand|k|M|B))?|\b[\d,]+\s*(?:USD|EUR|GBP|INR|dollars?|euros?|rupees?)\b/gi) || [];
  entities.MONEY = [...new Set(money)];

  // Percent
  const pcts = text.match(/\b\d+(?:\.\d+)?%|\d+(?:\.\d+)?\s*percent/gi) || [];
  entities.PERCENT = [...new Set(pcts)];

  // Dates
  const dates = text.match(/\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2}(?:st|nd|rd|th)?,?\s*\d{4}\b|\b\d{1,2}[-\/]\d{1,2}[-\/]\d{2,4}\b|\b\d{4}[-\/]\d{2}[-\/]\d{2}\b|\b(?:19|20)\d{2}\b/gi) || [];
  entities.DATE = [...new Set(dates)];

  // Numbers (standalone)
  const nums = text.match(/\b(?:\d{1,3}(?:,\d{3})+|\d+(?:\.\d+)?)\b/g) || [];
  entities.NUMBER = [...new Set(nums)].slice(0, 20);

  // Capitalized phrases = potential PERSON/ORG/LOCATION
  const capPhrases = text.match(/(?:[A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3})/g) || [];
  const phraseFreq = {};
  capPhrases.forEach(p => {
    p = p.trim();
    if (p.length < 4 || STOPWORDS.has(p.toLowerCase())) return;
    phraseFreq[p] = (phraseFreq[p] || 0) + 1;
  });

  const LOCATION_KEYWORDS = ['city','state','country','river','lake','mountain','ocean','sea','island','street','avenue','road','district','region','zone','village','town','capital'];
  const ORG_KEYWORDS = ['company','corp','inc','ltd','llc','organization','institute','university','college','school','foundation','association','department','ministry','agency','bank','group','team'];
  const PERSON_TITLES = ['dr','mr','mrs','ms','prof','sir','president','ceo','cto','director','manager','engineer','founder'];

  const sorted = Object.entries(phraseFreq).sort((a, b) => b[1] - a[1]).slice(0, 40);
  sorted.forEach(([phrase]) => {
    const lower = phrase.toLowerCase();
    const words = phrase.split(' ');
    const prevContext = text.toLowerCase().indexOf(lower) > 0 ? text.toLowerCase().slice(Math.max(0, text.toLowerCase().indexOf(lower) - 30), text.toLowerCase().indexOf(lower)) : '';

    if (PERSON_TITLES.some(t => prevContext.includes(t + ' ')) || (words.length >= 2 && words.length <= 3 && words.every(w => /^[A-Z]/.test(w)))) {
      entities.PERSON.push(phrase);
    } else if (ORG_KEYWORDS.some(k => lower.includes(k)) || /\b(?:Inc|Corp|Ltd|LLC|Co\.)\b/.test(phrase)) {
      entities.ORG.push(phrase);
    } else if (LOCATION_KEYWORDS.some(k => lower.includes(k)) || phrase.split(' ').length === 1) {
      entities.LOCATION.push(phrase);
    } else {
      entities.ORG.push(phrase); // fallback
    }
  });

  Object.keys(entities).forEach(k => {
    entities[k] = [...new Set(entities[k])].slice(0, 15);
  });

  return entities;
};

// ─────────────────────────────────────────────
// KEYWORD EXTRACTION (TF-IDF simplified)
// ─────────────────────────────────────────────

export const extractKeywords = (text, topN = 20) => {
  const words = tokenize(text).filter(w => w.length > 2 && !STOPWORDS.has(w));
  const totalWords = words.length;
  const freq = {};
  words.forEach(w => { freq[w] = (freq[w] || 0) + 1; });

  // TF
  const tf = {};
  Object.entries(freq).forEach(([w, c]) => { tf[w] = c / totalWords; });

  // IDF: penalize very common words (appear in >50% of sentences)
  const sentences = tokenizeSentences(text);
  const df = {};
  sentences.forEach(s => {
    const sWords = new Set(tokenize(s).filter(w => w.length > 2 && !STOPWORDS.has(w)));
    sWords.forEach(w => { df[w] = (df[w] || 0) + 1; });
  });

  return Object.entries(tf)
    .map(([word, tfVal]) => {
      const idf = Math.log((sentences.length + 1) / ((df[word] || 0) + 1)) + 1;
      const tfidf = tfVal * idf;
      return { word, tfidf: +tfidf.toFixed(5), freq: freq[word] };
    })
    .sort((a, b) => b.tfidf - a.tfidf)
    .slice(0, topN);
};

// ─────────────────────────────────────────────
// TOPIC MODELING (LDA-lite, pure JS)
// ─────────────────────────────────────────────

export const extractTopics = (text, numTopics = 5, wordsPerTopic = 8) => {
  const sentences = tokenizeSentences(text);
  if (sentences.length < numTopics) return [];

  const vocab = {};
  const docs = sentences.map(s => {
    const words = tokenize(s).filter(w => w.length > 3 && !STOPWORDS.has(w));
    words.forEach(w => { vocab[w] = (vocab[w] || 0) + 1; });
    return words;
  });

  // Filter vocab to mid-frequency words
  const validWords = Object.entries(vocab)
    .filter(([, c]) => c > 1 && c < sentences.length * 0.7)
    .map(([w]) => w);

  if (validWords.length < numTopics * wordsPerTopic) return [];

  // Random topic assignment
  const numWords = validWords.length;
  const topicWordCounts = Array.from({ length: numTopics }, () =>
    Object.fromEntries(validWords.map(w => [w, Math.random() * 2]))
  );

  // Simple co-occurrence based topic clustering
  const wordCooc = {};
  docs.forEach(doc => {
    const filtered = doc.filter(w => validWords.includes(w));
    for (let i = 0; i < filtered.length; i++) {
      for (let j = i + 1; j < Math.min(i + 4, filtered.length); j++) {
        const key = [filtered[i], filtered[j]].sort().join('|');
        wordCooc[key] = (wordCooc[key] || 0) + 1;
      }
    }
  });

  // Assign words to topics based on co-occurrence clusters
  const wordFreqEntries = validWords.map(w => ({ w, f: vocab[w] })).sort((a, b) => b.f - a.f);
  const seeds = wordFreqEntries.slice(0, numTopics).map(e => e.w);

  const topicAssignments = {};
  validWords.forEach(w => {
    let bestTopic = 0, bestScore = -1;
    seeds.forEach((seed, ti) => {
      const key = [w, seed].sort().join('|');
      const score = (wordCooc[key] || 0) + (w === seed ? 100 : 0);
      if (score > bestScore) { bestScore = score; bestTopic = ti; }
    });
    if (!topicAssignments[bestTopic]) topicAssignments[bestTopic] = {};
    topicAssignments[bestTopic][w] = (vocab[w] || 0) + (wordCooc[[w, seeds[bestTopic]].sort().join('|')] || 0) * 2;
  });

  const TOPIC_LABELS = ['Theme A', 'Theme B', 'Theme C', 'Theme D', 'Theme E', 'Theme F'];
  return Array.from({ length: numTopics }, (_, ti) => {
    const words = Object.entries(topicAssignments[ti] || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, wordsPerTopic)
      .map(([w]) => w);
    return { topic: TOPIC_LABELS[ti], words, coherence: +(words.length / wordsPerTopic).toFixed(2) };
  }).filter(t => t.words.length >= 3);
};

// ─────────────────────────────────────────────
// TEXT SIMILARITY (cosine on TF vectors)
// ─────────────────────────────────────────────

export const computeSimilarity = (text1, text2) => {
  const words1 = tokenize(text1).filter(w => !STOPWORDS.has(w));
  const words2 = tokenize(text2).filter(w => !STOPWORDS.has(w));
  const allWords = [...new Set([...words1, ...words2])];

  const freq1 = {}, freq2 = {};
  words1.forEach(w => { freq1[w] = (freq1[w] || 0) + 1; });
  words2.forEach(w => { freq2[w] = (freq2[w] || 0) + 1; });

  let dot = 0, mag1 = 0, mag2 = 0;
  allWords.forEach(w => {
    const a = freq1[w] || 0, b = freq2[w] || 0;
    dot += a * b; mag1 += a * a; mag2 += b * b;
  });

  const cosine = mag1 && mag2 ? dot / (Math.sqrt(mag1) * Math.sqrt(mag2)) : 0;

  // Jaccard
  const set1 = new Set(words1), set2 = new Set(words2);
  const intersection = new Set([...set1].filter(w => set2.has(w)));
  const union = new Set([...set1, ...set2]);
  const jaccard = union.size > 0 ? intersection.size / union.size : 0;

  // Overlap
  const overlap = Math.min(set1.size, set2.size) > 0 ? intersection.size / Math.min(set1.size, set2.size) : 0;

  return {
    cosine: +cosine.toFixed(4),
    jaccard: +jaccard.toFixed(4),
    overlap: +overlap.toFixed(4),
    sharedWords: [...intersection].slice(0, 15),
    uniqueToDoc1: [...set1].filter(w => !set2.has(w)).slice(0, 10),
    uniqueToDoc2: [...set2].filter(w => !set1.has(w)).slice(0, 10),
  };
};

// ─────────────────────────────────────────────
// CHARACTER-LEVEL ANALYSIS
// ─────────────────────────────────────────────

export const getCharAnalysis = (text) => {
  const freq = {};
  const lower = text.toLowerCase();
  for (const ch of lower) {
    if (/[a-z]/.test(ch)) freq[ch] = (freq[ch] || 0) + 1;
  }
  const total = Object.values(freq).reduce((s, v) => s + v, 0);
  const charFreq = Object.entries(freq)
    .sort((a, b) => b[1] - a[1])
    .map(([char, count]) => ({ char, count, pct: +(count / total * 100).toFixed(2) }));

  // Character class breakdown
  const digits = (text.match(/\d/g) || []).length;
  const spaces = (text.match(/\s/g) || []).length;
  const punctuation = (text.match(/[.,!?;:'"()\[\]{}\-—…]/g) || []).length;
  const uppercase = (text.match(/[A-Z]/g) || []).length;
  const lowercase = (text.match(/[a-z]/g) || []).length;
  const special = text.length - digits - spaces - uppercase - lowercase - punctuation;

  // Shannon entropy
  const totalChars = text.length;
  const entropy = -Object.values(freq).reduce((s, c) => {
    const p = c / totalChars; return s + (p > 0 ? p * Math.log2(p) : 0);
  }, 0);

  // Vowel/consonant ratio
  const vowels = (lower.match(/[aeiou]/g) || []).length;
  const consonants = (lower.match(/[bcdfghjklmnpqrstvwxyz]/g) || []).length;

  return {
    charFreq: charFreq.slice(0, 26),
    digits, spaces, punctuation, uppercase, lowercase, special,
    entropy: +entropy.toFixed(3),
    vowels, consonants,
    vowelRatio: +(vowels / (vowels + consonants || 1) * 100).toFixed(1),
  };
};

// ─────────────────────────────────────────────
// WORD LENGTH DISTRIBUTION
// ─────────────────────────────────────────────

export const getWordLengthDist = (text) => {
  const words = tokenize(text);
  const dist = {};
  words.forEach(w => { dist[w.length] = (dist[w.length] || 0) + 1; });
  return Object.entries(dist)
    .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
    .map(([len, count]) => ({ len: parseInt(len), count, pct: +(count / words.length * 100).toFixed(1) }));
};

// ─────────────────────────────────────────────
// SENTENCE LENGTH DISTRIBUTION
// ─────────────────────────────────────────────

export const getSentenceLengthDist = (text) => {
  const sentences = tokenizeSentences(text);
  return sentences.map((s, i) => ({
    idx: i + 1,
    words: tokenize(s).length,
    chars: s.length,
    label: s.slice(0, 40) + (s.length > 40 ? '...' : ''),
  }));
};

// ─────────────────────────────────────────────
// DOCUMENT FINGERPRINT / HASH
// ─────────────────────────────────────────────

export const getDocumentFingerprint = (text) => {
  // Simple checksum
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash |= 0;
  }
  const checksum = Math.abs(hash).toString(16).padStart(8, '0').toUpperCase();

  // Shingle similarity fingerprint (4-char shingles)
  const shingles = new Set();
  for (let i = 0; i < text.length - 3; i++) {
    shingles.add(text.slice(i, i + 4).toLowerCase());
  }

  // Vocabulary richness curve (type-token ratio over document)
  const words = tokenize(text);
  const ttrCurve = [];
  const seen = new Set();
  for (let i = 0; i < Math.min(words.length, 500); i += 10) {
    for (let j = i; j < Math.min(i + 10, words.length); j++) seen.add(words[j]);
    ttrCurve.push({ pos: i + 10, ttr: +(seen.size / (i + 10)).toFixed(3) });
  }

  return { checksum, shingleCount: shingles.size, ttrCurve };
};

// ─────────────────────────────────────────────
// PART-OF-SPEECH TAGGING (rule-based)
// ─────────────────────────────────────────────

const NOUN_SUFFIXES = ['tion','ment','ness','ity','ance','ence','ship','hood','dom','ism','ist','age','al','ure'];
const VERB_SUFFIXES = ['ate','ize','ise','ify','en','ing','ed','s'];
const ADJ_SUFFIXES = ['ful','less','ous','ive','al','ary','ic','able','ible','ent','ant','ish','ly'];

export const getPOSTags = (text) => {
  const words = tokenize(text);
  const counts = { NOUN: 0, VERB: 0, ADJ: 0, ADV: 0, OTHER: 0 };
  const tagged = words.slice(0, 200).map(w => {
    let tag = 'OTHER';
    if (w.endsWith('ly')) tag = 'ADV';
    else if (ADJ_SUFFIXES.some(s => w.endsWith(s) && w.length > s.length + 2)) tag = 'ADJ';
    else if (NOUN_SUFFIXES.some(s => w.endsWith(s) && w.length > s.length + 2)) tag = 'NOUN';
    else if (VERB_SUFFIXES.some(s => w.endsWith(s) && w.length > s.length + 1)) tag = 'VERB';
    else if (w.length > 4) tag = 'NOUN'; // long words tend to be nouns
    counts[tag]++;
    return { word: w, tag };
  });
  return { tagged, counts, total: words.length };
};

// ─────────────────────────────────────────────
// SUMMARIZATION (extractive)
// ─────────────────────────────────────────────

export const extractiveSummary = (text, numSentences = 5) => {
  const sentences = tokenizeSentences(text);
  if (sentences.length <= numSentences) return { sentences, scores: sentences.map((s, i) => ({ text: s, score: 1, idx: i })) };

  // Score sentences by: keyword density + position + length penalty
  const keywords = extractKeywords(text, 15).map(k => k.word);
  const maxLen = Math.max(...sentences.map(s => tokenize(s).length));

  const scored = sentences.map((s, i) => {
    const words = tokenize(s);
    const keywordScore = keywords.filter(k => words.includes(k)).length / keywords.length;
    const positionScore = i === 0 ? 1 : i === sentences.length - 1 ? 0.8 : 1 / (1 + i * 0.1);
    const lengthScore = Math.min(1, words.length / 20); // prefer medium-length sentences
    const score = keywordScore * 2 + positionScore + lengthScore;
    return { text: s, score: +score.toFixed(3), idx: i };
  });

  const top = [...scored].sort((a, b) => b.score - a.score).slice(0, numSentences);
  const summaryInOrder = top.sort((a, b) => a.idx - b.idx);

  return { sentences: summaryInOrder.map(s => s.text), scores: scored.slice(0, 30) };
};

// ─────────────────────────────────────────────
// REDUNDANCY / REPETITION DETECTION
// ─────────────────────────────────────────────

export const getRepetitionAnalysis = (text) => {
  const sentences = tokenizeSentences(text);
  const repetitions = [];

  for (let i = 0; i < sentences.length; i++) {
    for (let j = i + 1; j < sentences.length; j++) {
      const sim = computeSimilarity(sentences[i], sentences[j]);
      if (sim.cosine > 0.6) {
        repetitions.push({
          s1: sentences[i].slice(0, 60) + '...',
          s2: sentences[j].slice(0, 60) + '...',
          similarity: sim.cosine,
          i, j,
        });
      }
    }
  }

  // Repeated phrases (3+ word sequences)
  const words = tokenize(text);
  const phraseMap = {};
  for (let i = 0; i < words.length - 2; i++) {
    const phrase = words.slice(i, i + 3).join(' ');
    if (!phrase.split(' ').every(w => STOPWORDS.has(w))) {
      phraseMap[phrase] = (phraseMap[phrase] || 0) + 1;
    }
  }
  const repeatedPhrases = Object.entries(phraseMap)
    .filter(([, c]) => c > 2)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 15)
    .map(([phrase, count]) => ({ phrase, count }));

  return {
    nearDuplicateSentences: repetitions.slice(0, 10),
    repeatedPhrases,
    redundancyScore: +(repetitions.length / Math.max(1, sentences.length) * 100).toFixed(1),
  };
};

// ─────────────────────────────────────────────
// LINGUISTIC DIVERSITY METRICS
// ─────────────────────────────────────────────

export const getLinguisticDiversity = (text) => {
  const words = tokenize(text);
  const unique = new Set(words);

  // Moving-window TTR (more stable than global TTR)
  const windowSize = 100;
  const mattr = [];
  for (let i = 0; i <= words.length - windowSize; i += windowSize) {
    const window = words.slice(i, i + windowSize);
    mattr.push(new Set(window).size / windowSize);
  }
  const mattrScore = mattr.length > 0 ? mattr.reduce((s, v) => s + v, 0) / mattr.length : unique.size / words.length;

  // Hapax legomena (words appearing only once)
  const freq = {};
  words.forEach(w => { freq[w] = (freq[w] || 0) + 1; });
  const hapax = Object.values(freq).filter(c => c === 1).length;
  const hapaxRatio = hapax / unique.size;

  // Yule's K (vocabulary richness)
  const M1 = words.length;
  const freqOfFreq = {};
  Object.values(freq).forEach(c => { freqOfFreq[c] = (freqOfFreq[c] || 0) + 1; });
  const M2 = Object.entries(freqOfFreq).reduce((s, [f, n]) => s + parseInt(f) * parseInt(f) * n, 0);
  const yulesK = M1 > 0 ? 10000 * (M2 - M1) / (M1 * M1) : 0;

  // Herdan's C
  const herdanC = Math.log(unique.size) / Math.log(words.length) || 0;

  return {
    ttr: +(unique.size / words.length).toFixed(4),
    mattr: +mattrScore.toFixed(4),
    hapax, hapaxRatio: +hapaxRatio.toFixed(4),
    yulesK: +yulesK.toFixed(2),
    herdanC: +herdanC.toFixed(4),
    vocabulary: unique.size,
  };
};

// ─────────────────────────────────────────────
// DOCUMENT COMPARISON (multiple docs)
// ─────────────────────────────────────────────

export const compareDocuments = (docs) => {
  const n = docs.length;
  if (n < 2) return [];
  const matrix = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const sim = computeSimilarity(docs[i], docs[j]);
      matrix.push({ i, j, ...sim });
    }
  }
  return matrix;
};

// ─────────────────────────────────────────────
// GENERATE DEMO TEXTS
// ─────────────────────────────────────────────

export const DEMO_TEXTS = {
  'Tech Article': `Artificial intelligence is rapidly transforming the landscape of modern technology. Machine learning algorithms are becoming increasingly sophisticated, enabling computers to perform complex tasks that were previously thought to require human intelligence.

Deep learning, a subset of machine learning, uses neural networks with multiple layers to analyze data. These systems can recognize patterns in images, understand natural language, and even generate creative content. Companies like Google, Microsoft, and OpenAI are investing billions of dollars in AI research and development.

The ethical implications of artificial intelligence are profound and far-reaching. Questions about job displacement, privacy, and algorithmic bias need careful consideration. Researchers and policymakers are working together to develop frameworks that ensure AI systems are safe, transparent, and beneficial to society.

Natural language processing has seen remarkable advances in recent years. Large language models can now engage in sophisticated conversations, write code, analyze documents, and solve complex reasoning problems. These capabilities are being integrated into products and services across every industry.

The future of AI looks incredibly promising, but also raises important challenges. Ensuring that artificial intelligence benefits all of humanity, not just the privileged few, requires thoughtful policy and inclusive development practices. The decisions we make today about AI will shape the future for generations to come.`,

  'Business Report': `Q3 Financial Performance Summary: The company achieved exceptional revenue growth of 23% year-over-year, reaching $4.2 billion in total revenue. Operating margins improved significantly to 18.5%, up from 15.2% in the previous quarter.

Customer acquisition costs decreased by 12% while lifetime value increased by 31%, indicating improved product-market fit and customer retention strategies. The enterprise segment showed particularly strong growth, with 45 new Fortune 500 clients added during the quarter.

International expansion continued successfully, with revenue from markets outside North America growing 67% to represent 34% of total revenue. The APAC region showed the strongest growth at 89% YoY, driven by successful market entry in India and Southeast Asia.

Product investments are paying off. The new AI-powered features launched in July drove a 40% increase in daily active users and improved net promoter scores from 42 to 61. Customer support tickets decreased by 28% following the launch of the automated assistance system.

Looking ahead to Q4, we are forecasting revenue of $4.8 to $5.1 billion with operating margins of 19-21%. Key risks include macroeconomic headwinds, increased competition, and potential regulatory changes in the EU market. The board of directors approved a $500 million share buyback program to return value to shareholders.`,

  'Scientific Abstract': `Background: Neurodegenerative diseases affect millions of individuals worldwide, causing significant morbidity and mortality. Alzheimer's disease, Parkinson's disease, and ALS represent the most prevalent conditions, characterized by progressive loss of neuronal function.

Methods: We conducted a comprehensive meta-analysis of 127 randomized controlled trials published between 2015 and 2024, involving 89,432 patients. Studies were identified through systematic searches of PubMed, Cochrane, and EMBASE databases. Statistical analysis was performed using random-effects models with heterogeneity assessed by I² statistics.

Results: Novel biomarker-based diagnostic approaches demonstrated 94.3% sensitivity and 91.7% specificity for early-stage detection. Treatment with targeted molecular therapies showed significant reduction in disease progression rate (HR 0.67, 95% CI 0.58-0.77, p<0.001). Cognitive decline measured by MMSE scores was reduced by 2.4 points annually in the treatment group versus 4.1 points in the control group.

Conclusions: Early intervention using precision medicine approaches significantly improves patient outcomes in neurodegenerative diseases. The combination of biomarker screening and targeted therapy represents a paradigm shift in clinical management. Further research is needed to identify optimal treatment windows and patient stratification strategies.`,

  'News Article': `Global leaders gathered in Geneva yesterday for an emergency summit on climate change, as new scientific data revealed that carbon emissions rose by 2.1% last year despite previous commitments to reduce greenhouse gases. The meeting was attended by representatives from 195 countries, with developing nations demanding increased financial support from wealthy economies.

United Nations Secretary-General António Guterres called the situation "a code red for humanity," urging immediate and drastic action. The latest IPCC report warns that without significant reductions in emissions within the next seven years, the world faces irreversible climate damage affecting billions of people.

The European Union announced a new $300 billion green financing initiative, while the United States pledged to double its climate aid to developing countries by 2026. China, the world's largest emitter, committed to accelerating its transition to renewable energy, with President Xi Jinping announcing ambitious solar and wind expansion targets.

Critics argue that these pledges remain insufficient. Environmental activist group Extinction Rebellion organized protests in 40 cities, with demonstrators blocking major roads in London, Paris, and New York City. The protests highlighted the growing frustration among young people who feel world leaders are not acting with sufficient urgency.

Scientists from MIT and Stanford published research suggesting that geoengineering solutions, including solar radiation management, could buy additional time for emissions reductions. However, experts warned that these technologies carry significant risks and should not be seen as alternatives to fundamental changes in energy systems.`,
};